import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { PageHeader, Dropdown, Menu, Button, Badge, Modal } from 'antd';
import { EditOutlined, PlusOutlined, EllipsisOutlined, CloseOutlined } from '@ant-design/icons';

import * as ToolRequest from '../../tools/ToolRequest';
import * as ToolPermission from '../../tools/ToolPermission';

import SpinFull from '../../components/SpinFull';
import SuperTable from '../../components/SuperTable';
import CropNameBadge from '../../components/CropNameBadge';

export default function FarmCropViewScreen(props) {
  const history = useHistory();
  const userPermissionsData = useSelector(state => state.userPermissions);

  const isAllowedCrossCompany = ToolPermission.hasPermission(userPermissionsData, 'ui:company', 'get:xc');

  const [loaded, setLoaded] = useState(false);
  const [tableKey, setTableKey] = useState(Math.random().toString());
  const [searchCompanyOptions, setSearchCompanyOptions] = useState([]);

  const columns = [
    {
      template: 'action',
      key: 'action',
      width: 80,
      render: (value, record) => {
        return (
          <Dropdown
            overlay={
              <Menu onClick={value => {
                switch (value.key) {
                  case 'edit': {
                    history.push(`/farm-crop/edit/${record.id}`);
                    break;
                  }
                  case 'remove': {
                    Modal.confirm({
                      title: 'Confirm to REMOVE crop',
                      content: 'Are you sure to remove this crop?',
                      onOk: () => {
                        (async () => {
                          await ToolRequest.request('DELETE', `/v1/farm-crop/${record.id}`);

                          setTableKey(Math.random().toString());
                        })();
                      },
                    });
                    break;
                  }
                }
              }}>
                <Menu.Item key="edit">
                  <EditOutlined />
                  Edit
                </Menu.Item>
                <Menu.Item key="remove">
                  <CloseOutlined />
                  Remove
                </Menu.Item>
              </Menu>
            }
          >
            <Button
              type="default"
              shape="round"
            >
              <EllipsisOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
    {
      template: 'id',
      key: 'id',
      width: 40,
    },
    {
      key: 'name',
      title: 'Name',
      render: (value) => <CropNameBadge name={value} />,
    },
    {
      key: 'seedlingDayCount',
      title: 'Propagation Days',
      width: 130,
    },
    {
      key: 'plantingDayCount',
      title: 'Grow Days',
      width: 130,
    },
    {
      key: 'isEnabled',
      title: 'Status',
      width: 100,
      render: (value) => (
        <Badge status={value ? 'success' : 'default'} text={value ? 'Enabled' : 'Disabled'} />
      ),
    },
    {
      key: 'remarks',
      title: 'Remarks',
    },
  ];

  const searchColumns = [
    ...(isAllowedCrossCompany ? [{
      template: 'cascader',
      key: 'companyId',
      title: 'Company',
      options: searchCompanyOptions,
    }] : []),
    {
      template: 'cascader',
      key: 'isEnabled',
      title: 'Any Status',
      options: [
        {
          label: <Badge status="success" text="Enabled" />,
          value: true,
        },
        {
          label: <Badge status="default" text="Disabled" />,
          value: false,
        },
      ],
    },
  ];

  const searchQueryParams = values => {
    return {
      searchText: values.search ? `%${values.search}%` : undefined,
      companyId: values.companyId ? values.companyId[0] : undefined,
      isEnabled: values.isEnabled !== undefined ? values.isEnabled[0] : undefined,
    };
  };

  useEffect(() => {
    (async () => {
      if (isAllowedCrossCompany) {
        const companyRes = await ToolRequest.request('GET', '/v1/company', {
          count: 1000,
        });

        setSearchCompanyOptions(companyRes.data.map(company => ({
          value: company.id,
          label: company.displayName,
        })));
      }

      setLoaded(true);
    })();
  }, []);

  const loadDataSource = async (body) => {
    const cropRes = await ToolRequest.request('GET', '/v1/farm-crop', {
      ...body,
    });

    return {
      data: cropRes.data,
      totalCount: cropRes.totalCount,
    };
  };

  return loaded ? (
    <PageHeader
      title="Crop Catalog"
      extra={[
        <Button
          key="1"
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => history.push('/farm-crop/add')}
        >
          Add Crop
        </Button>
      ]}
    >
      <SuperTable
        showSearchInput={true}
        searchColumns={searchColumns}
        searchQueryParams={searchQueryParams}
        tableKey={tableKey}
        onTableKeyChange={key => setTableKey(key)}
        loadDataSource={loadDataSource}
        columns={columns}
      />
    </PageHeader>
  ) : <SpinFull />;
};
